import { useEffect, useRef } from 'react'
import { Link, getRouteApi } from '@tanstack/react-router'
import { BadgeCheck, ShieldAlert } from 'lucide-react'

import { AuthLayout } from '@/routes/login'
import { Button } from '@/components/ui/button'
import { useVerifyEmail } from '@/features/auth/api'
import { ApiError } from '@/lib/api'

const route = getRouteApi('/verifikasi-email')

export function VerifyEmailPage() {
  const { token } = route.useSearch()
  const verify = useVerifyEmail()
  const sudahDikirim = useRef(false)

  useEffect(() => {
    // Token hanya berlaku sekali, jadi permintaan tidak boleh terkirim dua
    // kali. React memasang efek dua kali pada mode ketat pengembangan, dan
    // tanpa penjaga ini percobaan kedua selalu gagal — memberi kesan
    // tautannya rusak padahal verifikasinya berhasil.
    if (!token || sudahDikirim.current) return
    sudahDikirim.current = true
    verify.mutate(token)
  }, [token, verify])

  if (!token) {
    return (
      <AuthLayout title="Tautan tidak lengkap" subtitle="Alamat yang Anda buka tidak memuat token.">
        <Keterangan
          nada="peringatan"
          judul="Token tidak ditemukan"
          isi="Salin ulang seluruh tautan dari surel yang Anda terima."
        />
      </AuthLayout>
    )
  }

  if (verify.isPending) {
    return (
      <AuthLayout title="Memverifikasi" subtitle="Sebentar, kami sedang memeriksa tautannya.">
        <div className="flex items-center gap-3 rounded-2xl border border-line p-5 text-muted">
          <span className="size-5 animate-spin rounded-full border-2 border-line border-t-brand" />
          Memeriksa tautan verifikasi…
        </div>
      </AuthLayout>
    )
  }

  if (verify.isError) {
    return (
      <AuthLayout title="Verifikasi gagal" subtitle="Tautannya sudah tidak berlaku.">
        <Keterangan
          nada="peringatan"
          judul="Tautan sudah tidak berlaku"
          isi={
            verify.error instanceof ApiError
              ? verify.error.message
              : 'Mintalah tautan baru dari halaman profil Anda.'
          }
        />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Surel terverifikasi" subtitle="Akun Anda siap dipakai bertransaksi.">
      <Keterangan
        nada="berhasil"
        judul="Alamat surel Anda sudah terverifikasi"
        isi="Terima kasih. Anda kini dapat membeli kelas dan mencairkan dana."
      />
    </AuthLayout>
  )
}

function Keterangan({ nada, judul, isi }: { nada: 'berhasil' | 'peringatan'; judul: string; isi: string }) {
  const berhasil = nada === 'berhasil'

  return (
    <div className="rounded-2xl border border-line p-5">
      <span
        className={`flex size-11 items-center justify-center rounded-xl ${
          berhasil ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning'
        }`}
      >
        {berhasil ? <BadgeCheck className="size-5.5" /> : <ShieldAlert className="size-5.5" />}
      </span>
      <p className="mt-4 font-bold text-ink">{judul}</p>
      <p className="mt-1.5 leading-relaxed text-muted">{isi}</p>

      <div className="mt-5 flex flex-wrap gap-2">
        <Link to={berhasil ? '/app' : '/app/profil'}>
          <Button variant={berhasil ? 'primary' : 'outline'}>
            {berhasil ? 'Buka dasbor' : 'Buka profil saya'}
          </Button>
        </Link>
      </div>
    </div>
  )
}
